import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, TextField, 
    Stack, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, TablePagination, InputAdornment, LinearProgress, Link
} from "@mui/material";
import { Add, Edit, Delete, Search, Campaign, AttachFile, CloudUpload } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage"; 
import { db, storage } from "../firebase"; // Ensure 'storage' is exported from your firebase config
import { format } from "date-fns";

function Notices() {
    const [notices, setNotices] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        date: format(new Date(), "yyyy-MM-dd"),
        academicYear: "2026",
        fileUrl: "",
        schoolId: "zIlbJM5BjyrlEhg7fWKs"
    });

    useEffect(() => { loadNotices(); }, []);

    const loadNotices = async () => {
        try {
            const q = query(collection(db, "notices"), orderBy("date", "desc"));
            const snap = await getDocs(q);
            setNotices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error(e); }
    };

    // --- Firebase Storage Upload Logic ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const storageRef = ref(storage, `notices/${formData.schoolId}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setProgress(prog);
            },
            (error) => {
                console.error(error);
                setUploading(false);
            },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                setFormData((prev) => ({ ...prev, fileUrl: url }));
                setUploading(false);
                setProgress(0);
            }
        );
    };

    const handleSave = async () => {
        if (!formData.title || !formData.description) return alert("Fill required fields");
        const docId = editId || doc(collection(db, "notices")).id;
        try {
            await setDoc(doc(db, "notices", docId), {
                ...formData,
                updatedAt: new Date().toISOString(),
                createdAt: formData.createdAt || new Date().toISOString()
            });
            setOpen(false);
            loadNotices();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id) => {
    // 1. Ask for confirmation
    if (window.confirm("Are you sure you want to delete this notice?")) {
        try {
            // 2. Delete from Firestore
            await deleteDoc(doc(db, "notices", id));
            
            // 3. Update local state immediately (Optimistic UI update)
            setNotices((prevNotices) => prevNotices.filter((notice) => notice.id !== id));
            
            // Optional: Success alert (using Swal if you have it elsewhere)
            console.log("Notice deleted successfully");
        } catch (error) {
            console.error("Error deleting document: ", error);
            alert("Failed to delete the notice. Please try again.");
        }
    }
};

    return (
        <Box p={2}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold"><Campaign color="primary" /> Notices</Typography>
                    <Stack direction="row" spacing={1}>
                        <TextField 
                            placeholder="Search..." size="small" value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditId(null); setOpen(true); }}>Add</Button>
                    </Stack>
                </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                            <TableCell>Sr.</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell align="center">File</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notices.filter(n => n.title.toLowerCase().includes(searchTerm.toLowerCase()))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((notice, index) => (
                                <TableRow key={notice.id} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>{notice.date}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{notice.title}</TableCell>
                                    <TableCell align="center">
                                        {notice.fileUrl && (
                                            <IconButton size="small" component="a" href={notice.fileUrl} target="_blank">
                                                <AttachFile fontSize="small" color="primary" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => { setEditId(notice.id); setFormData(notice); setOpen(true); }}><Edit fontSize="small" color="primary" /></IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(notice.id)}><Delete fontSize="small" color="error" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? "Edit Notice" : "New Notice"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} py={1}>
                        <TextField label="Date" type="date" fullWidth size="small" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} InputLabelProps={{ shrink: true }} />
                        <TextField label="Title" fullWidth size="small" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                        <TextField label="Description" multiline rows={3} fullWidth size="small" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                        
                        <Box border="1px dashed #ccc" p={2} textAlign="center" borderRadius={1}>
                            {uploading ? (
                                <Box>
                                    <Typography variant="caption">Uploading... {progress}%</Typography>
                                    <LinearProgress variant="determinate" value={progress} />
                                </Box>
                            ) : (
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                                    <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small">
                                        Upload File
                                        <input type="file" hidden onChange={handleFileUpload} />
                                    </Button>
                                    {formData.fileUrl && <Typography variant="caption" color="success.main">File Attached!</Typography>}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={uploading}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Notices;